import {CaretLeft, CaretRight, SignOut} from '@phosphor-icons/react';
import {Box, ClickAwayListener, Paper} from '@mui/material';
import {messages} from '@smartmixin/i18n';
import React from 'react';
import {useIntl} from 'react-intl';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router';
import Logo from '../../components/Logo';
import {IconButton} from '../../components/ui';
import RouterLink from '../../lib/RouterLink';
import {NavGroup, NavLink} from './NavLink';
import {MAIN_CONTENT_ID} from './SkipLink';
import {SidebarContent} from './SidebarContent';
import {shell} from '../../theme/tokens';
import {ThemeToggleButton} from './ThemeToggleButton';
import {useNavItems} from './useNavItems';

/**
 * Signed-in navigation at 900–1199px: a 64px strip of icon links (each named
 * by its destination, the same string as its tooltip). The toggle grows the
 * strip into the full 232px navigation, over the content and a backdrop (it
 * does not reflow the page) — one bar, not a second one beside the strip.
 * While open the strip is inert and hidden from assistive tech, so its icons
 * are not a second "Main" navigation. Focus moves to the panel's collapse
 * control; collapsing, Escape or the backdrop shrink it back and return
 * focus to the toggle; choosing a destination shrinks it and moves focus to
 * the page; tabbing out shrinks it. Not persisted: a reload starts collapsed.
 */
const NavRail = () => {
  const {formatMessage} = useIntl();
  const items = useNavItems();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState(false);
  const toggleRef = React.useRef<HTMLButtonElement>(null);
  const collapseRef = React.useRef<HTMLButtonElement>(null);
  // Where focus goes when the bar shrinks: back to the toggle (collapse,
  // Escape, backdrop), to the page (a destination was chosen: the clicked
  // link leaves with the panel), or nowhere (focus already left).
  const focusOnClose = React.useRef<'toggle' | 'main' | null>(null);
  const panelId = React.useId();

  const close = React.useCallback((focus: 'toggle' | 'main' | null) => {
    focusOnClose.current = focus;
    setExpanded(false);
  }, []);

  // Focus follows the bar: into the panel when it opens, out of it when it
  // shrinks. After the commit, because the toggle is inert while open (only
  // Chromium enforces that: the e2e collapse/Escape tests cover it, jsdom
  // does not); a layout effect, so focus never rests on <body> between.
  React.useLayoutEffect(() => {
    if (expanded) {
      collapseRef.current?.focus();
      return;
    }
    const target = focusOnClose.current;
    focusOnClose.current = null;
    if (target === 'toggle') {
      toggleRef.current?.focus();
    } else if (target === 'main') {
      document.getElementById(MAIN_CONTENT_ID)?.focus();
    }
  }, [expanded]);

  // Close when focus moves outside the rail and its panel (Tab out into the
  // page), so the panel never sits over content nobody is navigating.
  const railRef = React.useRef<HTMLDivElement>(null);
  const onBlur = React.useCallback(
    (e: React.FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (expanded && next && !railRef.current?.contains(next)) {
        close(null);
      }
    },
    [expanded, close],
  );

  React.useEffect(() => {
    if (!expanded) {
      return undefined;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close('toggle');
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [expanded, close]);

  const rail = (
    <Box
      data-testid="shell-rail"
      // inert removes it from the tab order and the accessibility tree;
      // aria-hidden repeats the latter for tools that ignore inert (jsdom).
      aria-hidden={expanded || undefined}
      inert={expanded}
      sx={{
        display: {xs: 'none', md: 'flex', lg: 'none'},
        flexDirection: 'column',
        alignItems: 'stretch',
        position: 'fixed',
        top: 0,
        bottom: 0,
        left: 0,
        width: shell.railWidth,
        px: 1,
        py: 1.5,
        gap: 1,
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        zIndex: (t) => t.zIndex.appBar,
      }}>
      <Box
        component={RouterLink}
        to="/"
        aria-label="SmartMixin"
        sx={{display: 'flex', justifyContent: 'center', py: 0.5}}>
        <Logo size={24} alt="" />
      </Box>
      <Box sx={{display: 'flex', justifyContent: 'center'}}>
        <IconButton
          ref={toggleRef}
          aria-label={formatMessage(messages.navigation.expandNavigation)}
          // True while open, though the strip is then inert: the state is
          // still the truth; the panel's collapse control is what is reached.
          aria-expanded={expanded}
          aria-controls={expanded ? panelId : undefined}
          onClick={() => setExpanded(true)}>
          <CaretRight />
        </IconButton>
      </Box>
      <Box
        component="nav"
        aria-label={formatMessage(messages.navigation.main)}
        sx={{display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 1}}>
        <NavGroup>
          {items.primary.map((item) => (
            <NavLink key={item.key} item={item} iconOnly />
          ))}
          <NavLink item={items.help} iconOnly />
          {items.admin.map((item) => (
            <NavLink key={item.key} item={item} iconOnly />
          ))}
        </NavGroup>
        <Box sx={{flexGrow: 1}} />
        <NavGroup>
          {items.footer.map((item) => (
            <NavLink key={item.key} item={item} iconOnly />
          ))}
        </NavGroup>
      </Box>
      <Box
        sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <ThemeToggleButton />
        <IconButton
          aria-label={formatMessage(messages.auth.logout)}
          onClick={() => {
            dispatch({type: 'LOGOUT'});
            navigate('/');
          }}>
          <SignOut />
        </IconButton>
      </Box>
    </Box>
  );

  // One stable tree whether or not the panel is open: swapping wrappers
  // would remount the rail, and focus could not return to its toggle.
  return (
    <ClickAwayListener onClickAway={() => expanded && close(null)}>
      <Box ref={railRef} onBlur={onBlur}>
        {rail}
        {expanded && (
          <>
            <Box
              data-testid="shell-rail-scrim"
              onClick={() => close('toggle')}
              sx={{
                display: {xs: 'none', md: 'block', lg: 'none'},
                position: 'fixed',
                inset: 0,
                bgcolor: 'scrim',
                zIndex: (t) => t.zIndex.drawer - 1,
              }}
            />
            {/* Over the strip, from the left edge: the strip itself grows. */}
            <Paper
              id={panelId}
              data-testid="shell-rail-panel"
              elevation={2}
              square
              sx={{
                display: {xs: 'none', md: 'block', lg: 'none'},
                position: 'fixed',
                top: 0,
                bottom: 0,
                left: 0,
                width: shell.sidebarWidth,
                overflowY: 'auto',
                zIndex: (t) => t.zIndex.drawer,
              }}>
              <SidebarContent
                onNavigate={() => close('main')}
                headerAction={
                  <IconButton
                    ref={collapseRef}
                    aria-label={formatMessage(
                      messages.navigation.collapseNavigation,
                    )}
                    aria-expanded
                    aria-controls={panelId}
                    onClick={() => close('toggle')}>
                    <CaretLeft />
                  </IconButton>
                }
              />
            </Paper>
          </>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default NavRail;
